import { useEffect, useRef } from 'react';
import {
  getEmbedParams,
  startIframeAutoResize,
  type IframeAutoResizeOptions,
} from './iframeResize';
import { getEmbedContext } from './embedContext';

/**
 * Reports this app's height to the parent frame for as long as the component
 * is mounted. No-op outside an iframe.
 *
 * Call it once, at the highest stable layout level — mounting it twice would
 * put two observers on the same element and post every update twice.
 *
 * The options are read once on mount, matching the underlying reporter, whose
 * configuration comes from URL parameters that cannot change without a reload.
 */
export function useIframeAutoResize(options: IframeAutoResizeOptions = {}): void {
  const { win, target } = options;

  // Read during RENDER, not in the effect below. Passive effects run
  // descendant-first, so a route that redirects on mount (`/staff` and
  // `/platform` render StaffBookingRedirectPage, which replaces the URL) wipes
  // the query string before the effect runs — and the frame would report as
  // anonymous for the rest of the session. This primes the reporter's
  // per-window memory while the original URL is still there. Idempotent, so
  // StrictMode's double render is harmless.
  const captured = useRef(false);
  if (!captured.current) {
    captured.current = true;
    getEmbedParams(win ?? window);
  }

  useEffect(
    // A v2 frame with a fixed or fit-to-screen height scrolls inside itself:
    // no height to report, and the viewport floor must stay.
    () => (getEmbedContext(win ?? window).autoHeight ? startIframeAutoResize({ win, target }) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start/stop once
    [],
  );
}
